/**
 * ComplianceStrip — 30-day story compliance table (RAISE-16886 T7).
 *
 * Table: Story | Gates | AR-Attest | Retro
 * Check (green) / X (red) per cell. Degrades independently with planGated chip.
 */

import { useTranslation } from "react-i18next";
import { useComplianceStrip } from "@/hooks/useComplianceStrip";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";

// ---------------------------------------------------------------------------
// Pass/Fail indicator
// ---------------------------------------------------------------------------

interface IndicatorProps {
  value: boolean;
}

function Indicator({ value }: IndicatorProps) {
  return value ? (
    <span
      className="text-green-600 dark:text-green-400 font-bold"
      title="pass"
      aria-label="yes"
    >
      ✓
    </span>
  ) : (
    <span
      className="text-destructive font-bold"
      title="fail"
      aria-label="no"
    >
      ✗
    </span>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface ComplianceStripProps {
  projectSlug: string;
}

export function ComplianceStrip({ projectSlug }: ComplianceStripProps) {
  const { t } = useTranslation();
  const { rows, loading, planGated } = useComplianceStrip(projectSlug);

  if (loading) {
    return (
      <div className="space-y-2" data-testid="compliance-strip-loading">
        <Skeleton className="h-8 rounded" />
        <Skeleton className="h-6 rounded" />
        <Skeleton className="h-6 rounded" />
      </div>
    );
  }

  if (planGated) {
    return (
      <div className="py-3 flex items-center gap-2" data-testid="compliance-strip-gated">
        <p className="text-sm text-muted-foreground">
          {t("projectPortal.governance.compliance.table", {
            defaultValue: "Compliance data",
          })}
        </p>
        <Badge variant="outline">
          {t("projectPortal.governance.compliance.planRequired", {
            defaultValue: "Plan required",
          })}
        </Badge>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="py-4 text-center" data-testid="compliance-strip-empty">
        <p className="text-sm text-muted-foreground">
          {t("projectPortal.governance.compliance.empty", {
            defaultValue: "No stories in the last 30 days.",
          })}
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto" data-testid="compliance-strip">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b">
            <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground">
              {t("projectPortal.governance.compliance.colStory", {
                defaultValue: "Story",
              })}
            </th>
            <th className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground">
              {t("projectPortal.governance.compliance.colGates", {
                defaultValue: "Gates",
              })}
            </th>
            <th className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground">
              {t("projectPortal.governance.compliance.colArAttest", {
                defaultValue: "AR-Attest",
              })}
            </th>
            <th className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground">
              {t("projectPortal.governance.compliance.colRetro", {
                defaultValue: "Retro",
              })}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.storyKey} className="border-b last:border-0">
              <td className="py-2 px-3 font-mono text-xs">{row.storyKey}</td>
              <td className="py-2 px-3 text-center">
                <Indicator value={row.gates} />
              </td>
              <td className="py-2 px-3 text-center">
                <Indicator value={row.arAttest} />
              </td>
              <td className="py-2 px-3 text-center">
                <Indicator value={row.retro} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
